import type { MockTables } from './mock-store';
import { mockStore } from './mock-store';
import { isMockBackend } from './supabase';

export interface SeedMeta {
  generated_at?: string;
  calibration_source?: string;
  calibration_window?: { start: string; end: string };
  history_window?: { start: string; end: string };
}

interface Snapshot extends MockTables, SeedMeta {}

// Bump this when seed snapshot format/content changes — forces all browsers to
// drop cached seed and re-fetch the new one.
const LOADED_KEY = 'wfp-v2:seed-loaded:v3-bq-precomputed';
const META_KEY = 'wfp-v2:seed-meta:v3-bq-precomputed';

export function getSeedMeta(): SeedMeta | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(META_KEY);
    return raw ? (JSON.parse(raw) as SeedMeta) : null;
  } catch {
    return null;
  }
}

export async function ensureSeedLoaded(): Promise<{ loaded: boolean; reason: string }> {
  if (!isMockBackend) return { loaded: false, reason: 'not-mock' };
  if (typeof window === 'undefined') return { loaded: false, reason: 'no-window' };
  if (window.localStorage.getItem(LOADED_KEY) === '1' && mockStore.getAll('warehouses').length > 0) {
    return { loaded: true, reason: 'cached' };
  }
  try {
    const res = await fetch('/seed-snapshot.json', { cache: 'no-cache' });
    if (!res.ok) {
      return { loaded: false, reason: `fetch-failed:${res.status}` };
    }
    const snap = (await res.json()) as Partial<Snapshot>;
    if (snap.warehouses) mockStore.replace('warehouses', snap.warehouses);
    if (snap.customers) mockStore.replace('customers', snap.customers);
    if (snap.day_calendar) mockStore.replace('day_calendar', snap.day_calendar);
    if (snap.historical_orders) mockStore.replace('historical_orders', snap.historical_orders);
    if (snap.forecast_adjustments)
      mockStore.replace('forecast_adjustments', snap.forecast_adjustments);
    if (snap.audit_log) mockStore.replace('audit_log', snap.audit_log);
    if (snap.config_values) mockStore.replace('config_values', snap.config_values);
    if (snap.forecast_results)
      mockStore.replace('forecast_results', snap.forecast_results);
    if (snap.review_decisions)
      mockStore.replace('review_decisions', snap.review_decisions);
    const meta: SeedMeta = {
      generated_at: snap.generated_at,
      calibration_source: snap.calibration_source,
      calibration_window: snap.calibration_window,
      history_window: snap.history_window,
    };
    window.localStorage.setItem(META_KEY, JSON.stringify(meta));
    window.localStorage.setItem(LOADED_KEY, '1');
    return { loaded: true, reason: 'fetched' };
  } catch (e) {
    return { loaded: false, reason: `error:${(e as Error).message}` };
  }
}

export function clearSeedCache(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LOADED_KEY);
  window.localStorage.removeItem(META_KEY);
  mockStore.reset();
}
